// A locale's short-date layout as an LDML pattern — the string every host
// hands the core's `format_date` (core/src/locale.rs).
//
// Pure and import-free on purpose: `scripts/record-date-vectors.mjs` runs it
// under plain Node to record `core/src/date_vectors.json`, where
// `./locale.js` (which reaches the core through `./core.js`) cannot load. So
// the pattern the vectors pin is the pattern the web hands in, by being the
// same function rather than a copy of it.
//
// `parts` is `Intl.DateTimeFormat(locale, { year, month, day: 'numeric' })
// .formatToParts(2026-01-02)` — the `yMd` skeleton, which is the question
// iOS's `setLocalizedDateFormatFromTemplate("yMd")` and Android's
// `getBestPattern("yMd")` ask of their own ICU. The probe's month and day are
// single digits so the width Intl renders says whether the locale pads: `M`
// for `1`, `MM` for `01`.
//
// Literal text is quoted whenever it holds a letter, so a literal can never
// be read as a field: `2.01.2026 г.` is `d.MM.y 'г.'`.

const FIELD = { year: 'y', month: 'M', day: 'd' };

export function ldmlFromParts(parts) {
  return (parts || [])
    .map((p) => {
      const letter = FIELD[p.type];
      if (letter === 'y') return 'y';
      if (letter) return letter.repeat(Math.min(2, String(p.value).length));
      const text = String(p.value ?? '');
      if (!text) return '';
      const quoted = text.replace(/'/g, "''");
      return /[A-Za-z\p{L}]/u.test(text) || text.includes("'") ? `'${quoted}'` : text;
    })
    .join('');
}

// The reference rendering: each stored part dropped into the slot Intl laid
// out, padded as the probe was, and the whole trimmed. The recorded vectors
// and `test/domain/datePattern.test.js` hold the core to it; the app does not
// call it.
export function substituteParts(parts, iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!m) return '';
  const fromIso = { year: m[1], month: m[2], day: m[3] };
  return (parts || [])
    .map((p) => {
      const value = fromIso[p.type];
      if (value === undefined) return p.value;
      return String(p.value).length === 1 ? String(Number(value)) : value;
    })
    .join('')
    .trim();
}
